import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { confirmAlert } from 'react-confirm-alert';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { refreshPrivateOfferAudienceEstimates, reviewPrivateOffer } from '../../api/private_offers';
import { formatAmountInCurrency } from '../../helpers/formatting';
import './PrivateOfferReview.scss';

const SCORE_GUIDE = [
  'No meaningful value: no useful saving for the required spend.',
  'Very weak: a small saving outweighed by spend or restrictions.',
  'Weak: some value, but a major limitation makes it unattractive.',
  'Modest: useful to a few shoppers; high spend or narrow eligibility.',
  'Fair: reasonable saving with ordinary spend and restrictions.',
  'Good: clear value at an achievable spend for the intended audience.',
  'Strong: generous saving with sensible spend and few restrictions.',
  'Very strong: unusually good value and easy for the audience to use.',
  'Exceptional: standout saving, accessible spend, minimal restrictions.',
  'Best in class: exceptional value across saving, spend, scope, and access.'
];
const SCORE_OPTIONS = SCORE_GUIDE.map((description, index) => ({ value: index + 1, label: `${index + 1} — ${description}`, description }));
const STRATEGIES = {
  new_customer_acquisition: 'Net-new',
  wishlisters: 'Net-new',
  category_prospects: 'Net-new',
  lapsed_customers: 'Winback'
};
const SYNC_LABELS = {
  ready: 'Full sync complete',
  pending: 'Waiting for full sync',
  unavailable: 'Customer matching unavailable',
  shopmy_only: 'ShopMy data only',
  not_required: 'Not required for this offer'
};
const dateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
const submittedDateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: '2-digit' });
const exactDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});
const ESTIMATE_KEYS = {
  new_customer_acquisition: 'acquisition',
  wishlisters: 'wishlister',
  category_prospects: 'categoryProspect',
  lapsed_customers: 'lapsedCustomer'
};
const REVIEW_EMAIL_TEMPLATE = 'private_offer_reviewed';
const REVISION_EMAIL_TEMPLATE = 'private_offer_revision_requested';
const DEFAULT_REVISION_INSTRUCTIONS = 'Private Offers are reserved for ShopMy’s highest-tier shoppers. To uphold the value of this exclusive access, we recommend structuring your Private Offer to deliver 30–70% savings.';

const RevisionDialog = ({ initialValue, onClose, onSend }) => {
  const [message, setMessage] = useState(initialValue);
  const [sendRevisionEmail, setSendRevisionEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [emailWarning, setEmailWarning] = useState('');
  const [saved, setSaved] = useState(false);
  const send = async () => {
    setSending(true);
    setSubmitError('');
    const result = await onSend(message.trim(), sendRevisionEmail);
    setSending(false);
    if (result.ok) {
      if (sendRevisionEmail && result.revisionEmailOutcome !== 'sent') {
        setEmailWarning('Revision request saved. No new brand email was sent; it may have been sent earlier for this revision, or delivery may be paused. Check Sends and Launches before retrying from the Revision Requested view.');
        setSaved(true);
      } else onClose();
    } else setSubmitError(result.error);
  };
  return (
    <div className='private-offer-review-dialog' role='dialog' aria-modal='true' aria-label='Request revisions'>
      <h2 className='private-offer-review-dialog-title'>Request revisions</h2>
      <p className='private-offer-review-dialog-copy'>The brand will see this message in its offer builder.</p>
      <label className='private-offer-review-dialog-label' htmlFor='review-revision-message'>Changes requested from the brand</label>
      <textarea className='private-offer-review-dialog-textarea' id='review-revision-message' rows={3} maxLength={2000} value={message} onChange={event => setMessage(event.target.value)} disabled={sending} autoFocus />
      <span className='private-offer-review-dialog-count'>{message.length}/2000</span>
      <label className='private-offer-review-dialog-email-option'>
        <input type='checkbox' checked={sendRevisionEmail} onChange={event => setSendRevisionEmail(event.target.checked)} disabled={sending} />
        Email the brand about this revision request
      </label>
      <p className='private-offer-review-dialog-copy'>Email sends only when this notification is enabled in Launches. During Shadow, the request is saved without emailing the brand.</p>
      <div className='private-offer-review-dialog-links'>
        <a className='private-offer-review-dialog-link' href={`/notifications?template=${REVISION_EMAIL_TEMPLATE}`} target='_blank' rel='noopener noreferrer'>Preview email template</a>
        <a className='private-offer-review-dialog-link' href={`/notifications?registry=${REVISION_EMAIL_TEMPLATE}`} target='_blank' rel='noopener noreferrer'>Edit registry copy</a>
      </div>
      {submitError && <p className='private-offer-review-dialog-copy' role='alert'>{submitError}</p>}
      {emailWarning && <p className='private-offer-review-dialog-copy' role='alert'>{emailWarning}</p>}
      <div className='private-offer-review-dialog-actions'>
        <button className='review-button' disabled={sending} onClick={onClose}>Cancel</button>
        {!saved && <button className='review-button review-publish' disabled={!message.trim() || sending} onClick={send}>{sending ? 'Sending…' : 'Send revision request'}</button>}
      </div>
    </div>
  );
};

const PrivateOfferReview = ({ offer, onReviewed, showOfferStatus = false }) => {
  const [score, setScore] = useState(offer.valueScore || null);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshedEstimates, setRefreshedEstimates] = useState(null);
  const [refreshingEstimate, setRefreshingEstimate] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const estimates = refreshedEstimates?.offerId === offer.id && refreshedEstimates?.baseEstimates === offer.audienceEstimates
    ? refreshedEstimates.estimates
    : offer.audienceEstimates;
  const approved = offer.publicationStatus === 'published';
  const syncStatus = offer.customerSyncStatus;
  const waitingForSync = syncStatus?.state === 'pending';
  const matchingUnavailable = syncStatus?.state === 'unavailable';
  const shopMyDataOnly = syncStatus?.state === 'shopmy_only';
  const syncHeld = waitingForSync || matchingUnavailable;
  const onlyNewCustomers = offer.customerAudience === 'new' ||
    ['new_customer_acquisition', 'wishlisters', 'category_prospects'].includes(offer.audienceStrategy) ||
    (offer.tiers.length > 0 && offer.tiers.every(tier => tier.customerAudience === 'new'));
  const offerHeldForSync = syncHeld && onlyNewCustomers;
  const ownStoreUnavailable = matchingUnavailable && syncStatus.unavailableStores?.some(store => Number(store.id) === Number(offer.BrandIntegration_id));
  const unavailableStoreNames = (syncStatus?.unavailableStores || []).map(store => store.domain || `Store ${store.id}`).join(', ');
  const syncLabel = SYNC_LABELS[syncStatus?.state] || 'Status unavailable';
  const syncHoldLabel = matchingUnavailable ? 'Customer matching unavailable' : 'Waiting for customer sync';
  const revisionRequested = offer.publicationStatus === 'revision_requested';
  const selectedScore = SCORE_OPTIONS.find(option => option.value === score);
  const brandUrl = offer.brand?.domain ? `https://${offer.brand.domain.replace(/^https?:\/\//i, '')}` : null;
  const unavailable = offer.status === 'ended' || !!offer.terminationRequestedAt;
  const money = amount => formatAmountInCurrency(Number(amount || 0), offer.currency || 'USD').replace(/\.00$/, '');
  const brandMoney = amount => (amount == null ? 'Not available' : formatAmountInCurrency(amount, 'USD'));
  const date = value => (value ? dateFormatter.format(new Date(value)) : 'Not set');
  const exactDate = value => (value ? `${exactDateFormatter.format(new Date(value))} (ET)` : 'On Approval');
  const tiers = [...offer.tiers].sort((a, b) => b.minimumShopperTier - a.minimumShopperTier);
  const strategy = STRATEGIES[offer.audienceStrategy] || 'Strategy not selected';
  const broadestTier = Math.max(...tiers.map(tier => Number(tier.minimumShopperTier)));
  const estimateKey = ESTIMATE_KEYS[offer.audienceStrategy];
  const audienceCount =
    [2, 3, 4].includes(broadestTier) && estimateKey ? estimates?.[`${estimateKey}${broadestTier === 2 ? 'Gold' : ''}Count`] : null;
  const audienceSource = estimates?.audienceDataTier;
  const estimateUnavailable = !!offer.estimateUnavailableReason;
  const audienceLabel = tier => {
    const level = Number(tier.minimumShopperTier);
    if (!tiers.some(row => Number(row.minimumShopperTier) === 1)) {
      if ([3, 4].includes(level)) return 'All Eligible Shoppers';
      if (level === 2) return 'Highest-Value Shoppers';
    }
    return `${tier.minimumShopperTierLabel || 'Eligible shopper'} Status`;
  };
  const terms = tier =>
    `Spend ${money(tier.minimumPurchaseAmount)}${tier.maximumPurchaseAmount != null ? `–${money(tier.maximumPurchaseAmount)}` : ''} • Save ${
      tier.isPercentage ? `${tier.rate}%` : money(tier.rate)
    }`;
  const products = offer.shopifyProductIds?.length
    ? `${offer.shopifyProductIds.length} product${offer.shopifyProductIds.length === 1 ? '' : 's'} only`
    : offer.shopifyCollectionIds?.length
    ? `${offer.shopifyCollectionIds.length} collection${offer.shopifyCollectionIds.length === 1 ? '' : 's'} only`
    : 'All products';
  const valuePercent = tier => {
    const percent = tier.isPercentage
      ? Number(tier.rate)
      : Number(tier.minimumPurchaseAmount) > 0
      ? (Number(tier.rate) / Number(tier.minimumPurchaseAmount)) * 100
      : null;
    return percent != null && Number.isFinite(percent) ? percent : null;
  };
  const decide = async (publicationStatus, revisionInstructions = '', sendRevisionEmail = false) => {
    setSaving(true);
    setError('');
    try {
      const result = await reviewPrivateOffer(offer, {
        publicationStatus,
        valueScore: score,
        expectedRevision: offer.reviewRevision,
        ...(publicationStatus === 'revision_requested' && { revisionInstructions, sendRevisionEmail })
      });
      onReviewed(result.offer);
      return { ok: true, revisionEmailOutcome: result.offer.revisionEmailOutcome };
    } catch (err) {
      const message = err.response?.data?.error || err.data?.error || 'Could not save this review. Please try again.';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setSaving(false);
    }
  };
  const refreshEstimate = async () => {
    setRefreshingEstimate(true);
    setEstimateError('');
    try {
      const result = await refreshPrivateOfferAudienceEstimates(offer.Brand_id);
      setRefreshedEstimates({ offerId: offer.id, baseEstimates: offer.audienceEstimates, estimates: result.estimates });
    } catch (err) {
      setEstimateError(err.response?.data?.error || 'Could not refresh this estimate. Please try again.');
    } finally {
      setRefreshingEstimate(false);
    }
  };
  const hasFutureStart = offer.claimStartsAt && new Date(offer.claimStartsAt).getTime() > Date.now();
  const approvalMessage = shopMyDataOnly
    ? `This offer will use ShopMy purchase data only and will ${hasFutureStart ? `go live on ${exactDate(offer.claimStartsAt)}` : 'go live when published'}. Shopify customer matching will stay off. This also applies if the brand previously enabled matching and then turned it off.`
    : ownStoreUnavailable
    ? 'Approval will be rejected until customer matching is restored for this offer’s store.'
    : syncHeld
      ? `You can approve this offer now. It will remain unavailable to new shoppers until ${matchingUnavailable ? 'customer matching is restored and ' : ''}the required full customer sync completes.${hasFutureStart ? ` Scheduled start: ${exactDate(offer.claimStartsAt)}.` : ''}`
      : hasFutureStart
        ? `This offer will go live on ${exactDate(offer.claimStartsAt)}.`
        : 'This offer will go live when published.';
  const confirmApproval = () =>
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className='private-offer-review-dialog' role='dialog' aria-modal='true' aria-label='Confirm approval'>
          <h2 className='private-offer-review-dialog-title'>Approve this offer?</h2>
          <p className='private-offer-review-dialog-copy'>{approvalMessage}</p>
          {!ownStoreUnavailable && <p className='private-offer-review-dialog-copy'>The brand will receive the <strong>{REVIEW_EMAIL_TEMPLATE}</strong> email with the approved outcome.</p>}
          {!ownStoreUnavailable && (
            <div className='private-offer-review-dialog-links'>
              <a className='private-offer-review-dialog-link' href={`/notifications?template=${REVIEW_EMAIL_TEMPLATE}`} target='_blank' rel='noopener noreferrer'>Preview email template</a>
              <a className='private-offer-review-dialog-link' href={`/notifications?registry=${REVIEW_EMAIL_TEMPLATE}`} target='_blank' rel='noopener noreferrer'>Edit registry copy</a>
            </div>
          )}
          <div className='private-offer-review-dialog-actions'>
            <button className='review-button' onClick={onClose}>Cancel</button>
            <button className='review-button review-publish' onClick={() => { onClose(); decide('published'); }}>Confirm approval</button>
          </div>
        </div>
      )
    });
  const confirmRevisionRequest = () =>
    confirmAlert({
      customUI: ({ onClose }) => <RevisionDialog initialValue={offer.revisionInstructions || DEFAULT_REVISION_INSTRUCTIONS} onClose={onClose} onSend={(message, sendRevisionEmail) => decide('revision_requested', message, sendRevisionEmail)} />
    });
  return (
    <tbody className='private-offer-review'>
      <tr className='review-summary'>
        <td className='review-cell review-submitted' title={offer.submittedAt ? exactDate(offer.submittedAt) : undefined}>
          {offer.submittedAt
            ? submittedDateFormatter.format(new Date(offer.submittedAt))
            : offer.publicationStatus
            ? 'Not recorded'
            : 'Not submitted'}
        </td>
        <td className='review-cell review-brand'>
          <strong className='review-strong'>
            {brandUrl ? (
              <a className='review-link' href={brandUrl} target='_blank' rel='noopener noreferrer'>
                {offer.brand?.name || `Brand ${offer.Brand_id}`}
              </a>
            ) : (
              offer.brand?.name || `Brand ${offer.Brand_id}`
            )}
          </strong>
          <button
            className='review-button review-expand'
            aria-expanded={expanded}
            aria-controls={`offer-details-${offer.id}`}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? 'Less detail' : 'More details'}
            <FontAwesomeIcon icon={faChevronDown} aria-hidden='true' />
          </button>
        </td>
        <td className='review-cell review-value'>
          {tiers.map(tier => {
            const percent = valuePercent(tier);
            return (
              <div className='review-tier' key={tier.id}>
                <div className='review-value-line'>
                  <strong className='review-strong'>{terms(tier)}</strong>
                  <span
                    className={cn('review-value-percent', { warning: percent !== null && percent < 30 })}
                    title='Savings as a percentage of the minimum spend'
                  >
                    {percent !== null ? `${percent.toLocaleString('en-US', { maximumFractionDigits: 1 })}% value` : 'Value unavailable'}
                  </span>
                </div>
                <small className='review-small'>
                  {audienceLabel(tier)} · {strategy}
                </small>
              </div>
            );
          })}
          {revisionRequested && offer.revisionInstructions && (
            <div className='review-requested-note'>
              <div className='review-requested-note-header'>
                <strong className='review-requested-note-title'>Requested changes</strong>
                {!unavailable && <button className='review-button review-edit-message' disabled={saving} onClick={confirmRevisionRequest}>Edit message</button>}
              </div>
              <p className='review-requested-note-copy'>{offer.revisionInstructions}</p>
            </div>
          )}
        </td>
        <td className='review-cell review-audience'>
          <strong className='review-strong'>{audienceCount != null ? `~${Number(audienceCount).toLocaleString('en-US')} shoppers` : estimateUnavailable ? 'Estimate unavailable' : 'Estimate pending'}</strong>
          <small className={cn('review-small', { 'review-sync-held': syncHeld })}>{shopMyDataOnly ? syncLabel : `Full sync verification: ${syncLabel}`}</small>
          {approved && syncHeld && <small className='review-small'>Approved · {syncHoldLabel}</small>}
          <small className='review-small'>
            {offer.estimateUnavailableReason || (audienceSource === 'verified'
              ? 'ShopMy + Shopify data'
              : audienceSource === 'observed'
              ? 'ShopMy spend data'
              : 'Data source unavailable')}
          </small>
          {!estimateUnavailable && (
            <button className='review-button review-refresh-estimate' disabled={refreshingEstimate} onClick={refreshEstimate}>
              {refreshingEstimate ? 'Updating estimate…' : 'Update estimate'}
            </button>
          )}
          {estimateError && <p className='review-message' role='alert'>{estimateError}</p>}
        </td>
        <td className='review-cell review-dates'>
          <span>{offer.claimStartsAt ? date(offer.claimStartsAt) : 'On Approval'}</span>
          <span className='review-arrow' aria-hidden='true'>
            →
          </span>
          <span>{date(offer.claimEndsAt)}</span>
        </td>
        {showOfferStatus && (
          <td className='review-cell review-offer-status'>
            <span className={cn('review-lifecycle', { live: offer.displayState === 'live' && !offerHeldForSync })}>
              {offerHeldForSync && ['live', 'upcoming'].includes(offer.displayState)
                ? syncHoldLabel
                : { live: 'Live', upcoming: 'Scheduled' }[offer.displayState] || 'Ended'}
            </span>
          </td>
        )}
        <td className='review-cell review-score' data-testid={`offer-review-score-${offer.id}`}>
          {approved || revisionRequested ? (
            <span title={selectedScore?.description}>{selectedScore ? `${score} — ${selectedScore.description.split(':')[0]}` : 'Not scored'}</span>
          ) : (
            <Select
              inputId={`quality-score-${offer.id}`}
              aria-label={`Quality score for offer ${offer.id}`}
              className='review-score-select'
              classNamePrefix='review-score-select'
              options={SCORE_OPTIONS}
              value={selectedScore || null}
              onChange={option => setScore(option.value)}
              isDisabled={saving || unavailable}
              isSearchable={false}
              placeholder='Select score'
              menuPosition='fixed'
              menuPlacement='auto'
              maxMenuHeight={520}
              formatOptionLabel={(option, { context }) =>
                context === 'value' ? (
                  `${option.value} — ${option.description.split(':')[0]}`
                ) : (
                  <span className='review-score-option'>
                    <b className='review-score-number'>{option.value}</b>
                    <span>{option.description}</span>
                  </span>
                )
              }
            />
          )}
        </td>
        <td className='review-cell review-actions'>
          {approved ? (
            <span className='review-approved'>Approved</span>
          ) : revisionRequested ? (
            <span className='review-requested-status'>Review requested</span>
          ) : (
            <>
              <button className='review-button review-publish' data-testid={`offer-review-approve-${offer.id}`} disabled={!score || saving || unavailable || offer.publicationStatus !== 'pending_review'} onClick={confirmApproval}>
                {saving ? 'Saving…' : 'Approve'}
              </button>
              <button className='review-button' disabled={saving || unavailable || !score || offer.publicationStatus !== 'pending_review'} onClick={confirmRevisionRequest}>
                Request revisions
              </button>
              {unavailable && <small className='review-small'>Offer ended or ending</small>}
              {matchingUnavailable && <small className='review-small'>Restore customer matching: {unavailableStoreNames}</small>}
            </>
          )}
          {error && <p className='review-message' role='alert'>{error}</p>}
        </td>
      </tr>
      {expanded && (
        <tr id={`offer-details-${offer.id}`}>
          <td className='review-cell' colSpan={showOfferStatus ? 8 : 7}>
            <div className='review-details'>
              <table className='review-detail-table' aria-label='Offer details'>
                <caption className='review-caption'>Offer details</caption>
                <tbody>
                  <tr>
                    <th className='review-label' scope='row'>Offer ID</th>
                    <td className='review-cell'>{offer.id}</td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Review version</th>
                    <td className='review-cell'>{offer.reviewRevision}</td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Strategy</th>
                    <td className='review-cell'>{strategy}</td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Value</th>
                    <td className='review-cell review-builder-tiers'>
                      {tiers.map(tier => (
                        <span className='review-builder-tier' key={tier.id}>
                          <span className='review-tier-label'>{audienceLabel(tier)}</span>
                          <span>{terms(tier)}</span>
                        </span>
                      ))}
                    </td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Store</th>
                    <td className='review-cell'>{offer.integration?.domain || 'No store selected'}</td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Products</th>
                    <td className='review-cell'>{products}</td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>{shopMyDataOnly ? 'Customer data' : 'Full sync verification'}</th>
                    <td className='review-cell'>
                      {syncLabel}
                      {matchingUnavailable && <small className='review-small'>Affected stores: {unavailableStoreNames}</small>}
                      {syncStatus?.verifiedThroughAt && <small className='review-small'>Verified through {exactDate(syncStatus.verifiedThroughAt)}</small>}
                      {syncStatus && !['not_required', 'shopmy_only'].includes(syncStatus.state) && <small className='review-small'>All required stores must complete a full sync after this offer’s audience was configured. Newer shopper accounts wait for a later sync.</small>}
                    </td>
                  </tr>
                  <tr>
                    <th className='review-label' scope='row'>Starts</th>
                    <td className='review-cell'>{exactDate(offer.claimStartsAt)}</td>
                  </tr>
                  {offer.claimEndsAt && (
                    <tr>
                      <th className='review-label' scope='row'>Ends</th>
                      <td className='review-cell'>{exactDate(offer.claimEndsAt)}</td>
                    </tr>
                  )}
                  {offer.maxClaims != null && (
                    <tr>
                      <th className='review-label' scope='row'>Max Codes</th>
                      <td className='review-cell'>{Number(offer.maxClaims).toLocaleString()}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <section>
                <table className='review-detail-table' aria-label='Brand details'>
                  <caption className='review-caption'>Brand details</caption>
                  <tbody>
                    <tr>
                      <th className='review-label' scope='row'>Brand ID</th>
                      <td className='review-cell'>{offer.Brand_id}</td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>Brand username</th>
                      <td className='review-cell'>{offer.brand?.brandSlug ? `@${offer.brand.brandSlug}` : 'Not set'}</td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>Account manager (CSM)</th>
                      <td className='review-cell'>{offer.brand?.accountManager || 'Unassigned'}</td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>Contact emails</th>
                      <td className='review-cell review-contact-emails'>
                        {offer.brandContactEmails?.length
                          ? offer.brandContactEmails.map(email => (
                              <a className='review-link' key={email} href={`mailto:${email}`}>
                                {email}
                              </a>
                            ))
                          : 'Not set'}
                      </td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>GMV · year to date</th>
                      <td className='review-cell'>{brandMoney(offer.brandPerformance?.gmv)}</td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>AOV · year to date</th>
                      <td className='review-cell'>{brandMoney(offer.brandPerformance?.aov)}</td>
                    </tr>
                    <tr>
                      <th className='review-label' scope='row'>Orders · year to date</th>
                      <td className='review-cell'>
                        {offer.brandPerformance?.orderCount != null ? offer.brandPerformance.orderCount.toLocaleString('en-US') : 'Not available'}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <small className='review-small review-metric-context'>
                  Year-to-date attributed partner sales · USD.
                  {offer.brandPerformance?.updatedAt && ` Updated ${exactDate(offer.brandPerformance.updatedAt)}.`}
                </small>
                {offer.publicationReviewedAt && <small className='review-small'>Reviewed {exactDate(offer.publicationReviewedAt)}</small>}
              </section>
            </div>

            {offer.revisionInstructions && !revisionRequested && (
              <section className='review-revision-request' aria-label='Previous revision request'>
                <strong>Previous revision request</strong>
                <p className='review-revision-text'>{offer.revisionInstructions}</p>
              </section>
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
};
PrivateOfferReview.propTypes = {
  offer: PropTypes.object.isRequired,
  onReviewed: PropTypes.func.isRequired,
  showOfferStatus: PropTypes.bool
};

export default PrivateOfferReview;
